import { useDebouncedEffect } from '@react-hookz/web';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Icon from '~/components/Icon/Icon';
import Modal from '~/components/Modal/Modal';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import Spinner from '~/components/Spinner/Spinner';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { mainStore, settingStore, userStore } from '~/stores';
import { getAddressModalType } from '~/utils/addressModal';

import { DateField } from './components/DateField';
import { MIN_AGE } from './constants';
import styles from './ModalBirthDate.module.scss';
import { getAgeByDate } from './utils';

const ModalBirthDate = () => {
  const { isMobile } = useGlobal();
  const { closeModal, openModal } = useModal();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [value, setValue] = useState(userStore.dateOfBirth ?? '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const onCloseClick = () => {
    closeModal([REDIRECT_QUERY_NAME]);
  };

  const handleComplete = () => {
    setIsLoading(true);
    userStore
      .updatePersonalData({ date_of_birth: value })
      .then(() => {
        setIsLoading(false);
        mainStore.pushAlert(
          'success',
          userStore.personalData.isAdult
            ? t('phrases:dokobitAuthSuccessAdult')
            : t('phrases:dokobitAuthSuccess'),
        );
        const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);

        if (redirectNext) {
          if (redirectNext === '/cart' && !userStore.deliveryAddress) {
            const targetModalType = getAddressModalType();

            openModal(
              targetModalType,
              'modal-birth-date',
              {
                [REDIRECT_QUERY_NAME]: redirectNext,
              },
              false,
              true,
            );
            return;
          }

          navigate(redirectNext, { replace: true });
          return;
        }

        closeModal();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  useDebouncedEffect(
    () => {
      if (validateDate(value)) {
        const currentAge = getAgeByDate(new Date(value));

        if (currentAge <= 0 || currentAge >= 150) {
          setIsValid(false);
          setError(t('errors:invalidAge'));
          return;
        }

        setIsValid(true);

        const minAge = settingStore.maxMinAge || MIN_AGE;

        if (currentAge < minAge) {
          setError(t('errors:ageRestriction', { age: minAge }));
        } else {
          setError('');
        }
      } else {
        setError(t('errors:invalidAge'));
      }
    },
    [value],
    500,
  );

  useEffect(() => {
    const cleanUp = () => {
      if (document.body.classList.contains(`${styles.noScroll}`)) {
        document.body.classList.remove(`${styles.noScroll}`);
      }
    };

    if (userStore.personalData.dateOfBirth) {
      cleanUp();
      closeModal();
      return;
    }

    document.body.classList.add(`${styles.noScroll}`);
    return cleanUp;
  }, []);

  return (
    <Modal
      isShow={true}
      className={classes([styles.root, isMobile && '_mobile-fullscreen'])}
      onBackdropDismiss={onCloseClick}
    >
      <div className={styles.header}>
        <button
          data-button="back"
          className="button _no-padding _no-color input-form__back"
          onClick={onCloseClick}
        >
          <Icon type="close" size={30} />
        </button>
      </div>
      <div className={styles.body}>
        <div>
          <h2 className={styles.headingLabel}>
            {t('phrases:yourDateOfBirth')}
          </h2>
          <p className={styles.captionLabel}>{t('phrases:enterDateOfBirth')}</p>
        </div>

        <div className={styles.content}>
          <div className={styles.dateInput}>
            <DateField
              required
              name="birth-date"
              value={value}
              onChange={setValue}
              dataName="date-of-birth-input"
              setIsValid={(value) => {
                if (!value) {
                  setError('');
                }
                setIsValid(value);
              }}
            />
            {error && <span className={styles.errorText}>{error}</span>}
          </div>
        </div>

        <div className={styles.actions}>
          <button
            data-button="close-date-of-birth-modal"
            className="button _bordered"
            onClick={onCloseClick}
          >
            {t('skip')}
          </button>
          <button
            data-button="save-date-of-birth"
            className={classes([
              'button _primary',
              (!isValid || !value) && '_disabled',
            ])}
            disabled={!isValid || !value}
            onClick={handleComplete}
          >
            {t(isMobile ? 'complete' : 'apply')}
          </button>
        </div>
      </div>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <Spinner />
        </div>
      )}
    </Modal>
  );
};

const validateDate = (value: string) => {
  const formatRegex = /\d{4}-\d{2}-\d{2}/g;

  const match = value.match(formatRegex);

  if (!match) {
    return false;
  }

  const [year, month, day] = value.split('-');

  if (!year || !month || !day) {
    return false;
  }

  const thisYear = new Date().getFullYear();

  return (
    Number(year) > 1900 &&
    Number(year) < thisYear &&
    Number(month) > 0 &&
    Number(month) <= 12 &&
    Number(day) > 0 &&
    Number(day) <= 31
  );
};

export default observer(ModalBirthDate);
